import { listAccessibleTeams } from './teamsService';
import { getDataService } from './dataServiceFactory';

interface MappingLike {
  mapping?: {
    controlKey?: string;
    mappedControlKey?: string;
  };
}

interface VisibilityContext {
  ownerByFrameworkKey: Map<string, string | undefined>;
  callerTeamIds: Set<string>;
}

let cachedContext: Promise<VisibilityContext> | null = null;

// Fail closed: propagate errors from framework/team lookups so callers can
// surface the failure rather than silently over- or under-sharing data.
const buildContext = async (): Promise<VisibilityContext> => {
  const [frameworks, teams] = await Promise.all([
    getDataService().getAvailableFrameworks(),
    listAccessibleTeams(),
  ]);
  const ownerByFrameworkKey = new Map<string, string | undefined>();
  for (const f of frameworks) {
    const key = (f as any).frameworkKey
      ?? `${(f as any).frameworkName ?? ''}#${(f as any).version ?? ''}`;
    ownerByFrameworkKey.set(key, (f as any).ownerBindleResource);
  }
  return {
    ownerByFrameworkKey,
    callerTeamIds: new Set(teams.map((t) => t.groupId)),
  };
};

const getVisibilityContext = (): Promise<VisibilityContext> => {
  if (!cachedContext) {
    const p = buildContext().catch((err) => {
      if (cachedContext === p) cachedContext = null;
      throw err;
    });
    cachedContext = p;
  }
  return cachedContext;
};

const frameworkKeyFromControlKey = (controlKey?: string): string | null => {
  if (!controlKey) return null;
  const parts = controlKey.split('#');
  if (parts.length < 2) return null;
  return `${parts[0]}#${parts[1]}`;
};

// Visible only when the viewer's teams can unlock BOTH frameworks the mapping
// touches. Legacy frameworks with no ownerBindleResource act as shared/public.
const isSideVisible = (ctx: VisibilityContext, frameworkKey: string | null): boolean => {
  if (!frameworkKey) return true;
  const owner = ctx.ownerByFrameworkKey.get(frameworkKey);
  if (!owner) return true;
  return ctx.callerTeamIds.has(owner);
};

export const filterVisibleMappings = async <T extends MappingLike>(
  items: T[]
): Promise<T[]> => {
  const ctx = await getVisibilityContext();
  return items.filter((item) => {
    const fkA = frameworkKeyFromControlKey(item.mapping?.controlKey);
    const fkB = frameworkKeyFromControlKey(item.mapping?.mappedControlKey);
    return isSideVisible(ctx, fkA) && isSideVisible(ctx, fkB);
  });
};

export const resetMappingVisibilityCache = (): void => {
  cachedContext = null;
};
